import { useEffect, useState } from 'react';
import { getBreakpointIdentifier, getCookie, toTitleCase, variableCheckPromise } from './helpers';
import { EventName, postUiLog } from './uilog';

const GATSBY_CORVETTE_HOST = process.env.GATSBY_CORVETTE_HOST;

const PERSONALIZATION_BUCKET_WAIT = 2000;
const GATSBY_HYDRATION_FALLBACK_WAIT = 2500;

let gatsbyHydrationCompleteDispatched = false;
let gatsbyHydrationCompleteFallbackTimer: number | null = null;
let gatsbyHydrationManagedByHook = false;
let returnUserFallbackUiLogPosted = false;
let lastRawPersonalizationBucket: unknown = null;

const postReturnUserFallbackUiLog = (placement: string): void => {
  if (returnUserFallbackUiLogPosted) {
    return;
  }

  returnUserFallbackUiLogPosted = true;

  postUiLog(EventName.alerts, {
    placement,
    category: 'bucket-unavailable-new-user-fallback',
    reason: 'atjs-failure-or-bucket-invalid',
    bucket: lastRawPersonalizationBucket ?? null
  });
};

const dispatchGatsbyHydrationComplete = (_reason?: string) => {
  if (
    typeof window === 'undefined' ||
    gatsbyHydrationCompleteDispatched ||
    window.gatsbyHydrationComplete
  ) {
    return;
  }

  gatsbyHydrationCompleteDispatched = true;

  if (gatsbyHydrationCompleteFallbackTimer !== null) {
    window.clearTimeout(gatsbyHydrationCompleteFallbackTimer);
    gatsbyHydrationCompleteFallbackTimer = null;
  }

  const event = new CustomEvent('gatsbyHydrationComplete', {
    bubbles: true,
    cancelable: false
  });

  window.dispatchEvent(event);
  window.gatsbyHydrationComplete = true;
};

export const scheduleGatsbyHydrationCompleteFallback = (
  reason = 'no return user or alerts nav elements - hydration fallback event',
  delay = GATSBY_HYDRATION_FALLBACK_WAIT
) => {
  if (
    typeof window === 'undefined' ||
    gatsbyHydrationCompleteDispatched ||
    window.gatsbyHydrationComplete
  ) {
    return;
  }

  if (gatsbyHydrationCompleteFallbackTimer !== null) {
    window.clearTimeout(gatsbyHydrationCompleteFallbackTimer);
  }

  gatsbyHydrationCompleteFallbackTimer = window.setTimeout(() => {
    gatsbyHydrationCompleteFallbackTimer = null;

    if (
      gatsbyHydrationManagedByHook ||
      gatsbyHydrationCompleteDispatched ||
      window.gatsbyHydrationComplete
    ) {
      return;
    }

    dispatchGatsbyHydrationComplete(reason);
  }, delay);
};

let personalizationBucketRequest: Promise<number | null> | null = null;
let cachedPersonalizationBucket: number | undefined;

const normalizeBucketValue = (bucketRaw: unknown): number | null => {
  const bucket = Number(bucketRaw);
  return Number.isInteger(bucket) && bucket >= 1 && bucket <= 10 ? bucket : null;
};

const getPersonalizationBucket = async (
  timeout = PERSONALIZATION_BUCKET_WAIT
): Promise<number | null> => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (cachedPersonalizationBucket !== undefined) {
    return cachedPersonalizationBucket;
  }

  if (personalizationBucketRequest) {
    return personalizationBucketRequest;
  }

  personalizationBucketRequest = (async () => {
    const bucket = await variableCheckPromise(() => {
      lastRawPersonalizationBucket = (window as any)?.personalization?.bucket;
      return normalizeBucketValue(lastRawPersonalizationBucket);
    }, timeout);

    // Cache only successful bucket reads. If bucket was unavailable,
    // allow future calls to retry instead of permanently caching null.
    if (bucket !== null) {
      cachedPersonalizationBucket = bucket;
    }

    return bucket;
  })().finally(() => {
    personalizationBucketRequest = null;
  });

  return personalizationBucketRequest;
};

// Remember to remove this flag once the hardcoded return experiences are stable and working as expected.
// If you need to quickly disable repo-side return-user determination, temporarily set this to false.
// When false, repo-side return-user/token/profile/alerts logic is disabled and buckets 1-10 are treated
// as Target-handled so Target-delivered code can continue to execute.
const ENABLE_RETURN_USER_TEMP_FLAG = true;

const isRepoSideReturnUserEnabled = (): boolean => ENABLE_RETURN_USER_TEMP_FLAG;

const isRepoBucket = (bucket: number | null): boolean =>
  isRepoSideReturnUserEnabled() && bucket === 1;

const isTargetHandledBucket = (bucket: number | null): boolean =>
  bucket !== null &&
  bucket >= (isRepoSideReturnUserEnabled() ? 2 : 1) &&
  bucket <= 10;

interface LimitedToken {
  accessToken: string;
  tokenType: string;
  expiresIn: number; // seconds
  idleExp: number; // seconds
}

interface TokenResponse {
  limitedToken: LimitedToken;
  redirectType: string;
}

interface UserSession {
  token: string;
  savedAt: number;
  idleExp: number;
  marketingId: string;
  userName: string | null;
  alertsCount: number | null;
}

// Shared in-flight requests, this prevents multiple API requests if multiple components request at the same time token, user name or alerts number
let ongoingTokenRequest: Promise<string | null> | null = null;
let ongoingUserRequest: Promise<string | null> | null = null;
let ongoingAlertsRequest: Promise<number | null> | null = null;

function isTokenValid(session: UserSession) {
  const deltaMs = 1 * 60 * 1000; // 1 minute in milliseconds
  const now = Date.now();
  const expiryTime = session.savedAt + session.idleExp * 1000;
  return now < expiryTime - deltaMs;
}

const SESSION_KEY = 'userSession';

// --- MARKETING ID ---
export function getMarketingId(): string | null {
  if (typeof window === 'undefined') return null;

  // 1. Check URL for ?mkid=
  const urlParams = new URLSearchParams(window.location.search);
  const mkidParam = urlParams.get('mkid');
  if (mkidParam && isValidMarketingId(mkidParam)) {
    return mkidParam;
  }

  // 2. Direct cookie: marketingid
  const marketingIdCookie = getCookie('marketingid');
  if (marketingIdCookie && isValidMarketingId(marketingIdCookie)) {
    return marketingIdCookie;
  }

  // 3. tmsData cookie
  const tmsDataCookie = getCookie('tmsData');
  const midFromTms = getMarketingIdFromTmsData(tmsDataCookie);
  if (midFromTms) {
    return midFromTms;
  }
  return null;
}

function getMarketingIdFromTmsData(tmsDataCookie: string | null): string | null {
  if (!tmsDataCookie) return null;

  try {
    const parsed = JSON.parse(tmsDataCookie);
    const mid = parsed?.member?.marketingid;
    if (mid && isValidMarketingId(mid)) return mid;
    return null;
  } catch (err) {
    console.warn('Failed to parse tmsData cookie, ignoring it', err);
    return null; // safely ignore parse errors
  }
}

export function isValidMarketingId(id: string): boolean {
  // Standard GUID regex
  const guidRegex = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;

  if (!guidRegex.test(id)) return false;

  // Disallow all zeros: 00000000-0000-0000-0000-000000000000
  if (/^0{8}-(0{4}-){3}0{12}$/.test(id)) return false;

  return true;
}

// --- TOKEN ---
export async function getToken({
  mid = null,
  force = false
}: {
  mid?: string | null;
  force?: boolean;
} = {}): Promise<string | null> {
  let marketingId: string | null | undefined = mid;
  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }
   if (session && (!mid || session.marketingId === mid) && !force && isTokenValid(session)) {
     return session.token;
   }

  if (ongoingTokenRequest) return ongoingTokenRequest;

  ongoingTokenRequest = (async () => {
    if (!marketingId) {
      marketingId = getMarketingId();
    }

    if (!marketingId) {
      ongoingTokenRequest = null;
      return null;
    }

    try {
      const res = await fetchTokenFromApi(marketingId);

      session = {
        token: res.limitedToken.accessToken,
        savedAt: Date.now(),
        idleExp: res.limitedToken.idleExp,
        marketingId,
        userName: null,
        alertsCount: null
      };

      sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));

      return session.token;
    } catch (err) {
      console.error('Failed to fetch token', err);

      const alertTimingFields = await getAlertTimingFields();

      postUiLog(EventName.alerts, {
        placement: 'nav-public-header-alerts',
        category: 'alert-NaN',
        ...alertTimingFields
      });

      return null;
    } finally {
      ongoingTokenRequest = null;
    }
  })();

  return ongoingTokenRequest;
}

async function fetchTokenFromApi(marketingId: string): Promise<TokenResponse> {
  const payload = {
    clientId: 'experian',
    authorizationLevel: 'AL_100',
    marketingId
  };

  const sessionId = await variableCheckPromise(() => window?.ECS?.tms?.data?.sessionid);

  const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/securelogin/limited/access/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json; charset=UTF-8',
      'x-session-id': sessionId || ''
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) throw new Error('Token request failed');
  return response.json();
}

// --- USER NAME ---
type UIProfileAPIResponse = {
  customer: { name: { first: string } };
};

export async function getUserName(token: string): Promise<string | null> {
  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }

  if (session?.userName) {
    return toTitleCase(session.userName);
  }

  // If a request is already in flight, reuse it
  if (ongoingUserRequest) return ongoingUserRequest;

  ongoingUserRequest = (async () => {
    try {
      const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/uiprofile`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`
        }
      });

      // Silently return null if request fails
      if (!response.ok) {
        return null;
      }

      const data: UIProfileAPIResponse = await response.json();
      const name = toTitleCase(data.customer?.name?.first);

      if (session) {
        session.userName = name;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }

      return name;
    } catch (err) {
      console.error('Failed to fetch user name', err);
      return null;
    } finally {
      ongoingUserRequest = null;
    }
  })();

  return ongoingUserRequest;
}

// --- ALERTS ---
type AlertsAPIResponse = {
  data?: {
    eCommerceUserInfo?: {
      customRetargeting?: { unreadAlertCount?: number };
    };
  };
};

type AlertTimingFields = {
  lastLoginTime: number;
  refreshTime: number;
};

async function getAlertTimingFields(): Promise<AlertTimingFields> {
  const lastLoginTimeRaw = await variableCheckPromise(
    () => (window as any)?.personalization?.lastLoginTime
  );
  const lastLoginTime = Number(lastLoginTimeRaw || 0);

  const now = new Date();
  let refreshDate = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 9, 40, 0)
  );

  if (now < refreshDate) {
    refreshDate.setUTCDate(refreshDate.getUTCDate() - 1);
  }

  const refreshTime = refreshDate.getTime();

  return {
    lastLoginTime,
    refreshTime
  };
}

export async function getAlertsCount(token: string): Promise<number | null> {
  const alertTimingFields = await getAlertTimingFields();
  const { lastLoginTime, refreshTime } = alertTimingFields;

  // If user logged in after refresh, clear session and return null
  if (lastLoginTime !== 0 && lastLoginTime >= refreshTime) {
    sessionStorage.removeItem(SESSION_KEY);
    return null;
  }

  // Load session from storage
  let session: UserSession | null = null;
  const sessionStr = sessionStorage.getItem(SESSION_KEY);
  if (sessionStr) {
    try {
      session = JSON.parse(sessionStr);
    } catch (err) {
      sessionStorage.removeItem(SESSION_KEY);
      session = null;
    }
  }

  // If token matches and alerts are cached, return cached value
  if (session?.alertsCount != null) {
    return session.alertsCount;
  }

  // Always fetch alerts if token is new or cache missing
  if (ongoingAlertsRequest) return ongoingAlertsRequest;

  ongoingAlertsRequest = (async () => {
    try {
      const query = `
        query customer {
          eCommerceUserInfo {
            customRetargeting {
              unreadAlertCount
            }
          }
        }
      `;

      const response = await fetch(`${GATSBY_CORVETTE_HOST}/api/router/graphql`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ query, operationName: 'customer' })
      });

      // Silently return null if request fails
      if (!response.ok) return null;

      const data: AlertsAPIResponse = await response.json();
      const count = data?.data?.eCommerceUserInfo?.customRetargeting?.unreadAlertCount ?? 0;

      // Update session storage
      if (session) {
        session.alertsCount = count; // update alerts
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
      }

      return count;
    } catch (err) {
      console.error('Failed to fetch alerts', err);
      postUiLog(EventName.alerts, {
        placement: 'nav-public-header-alerts',
        category: 'alert-NaN',
        ...alertTimingFields
      });
      return null;
    } finally {
      ongoingAlertsRequest = null;
    }
  })();

  return ongoingAlertsRequest;
}
// use it on pages with return user experiences

const getQueryParam = (key: string): string | null => {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(key);
};

const normalizeParamValue = (value?: string | null): string => (value ?? '').trim().toLowerCase();

export function useReturnUser(blockedCcParams?: string[], includeRefParams?: string[]) {
  const [loading, setLoading] = useState(isRepoSideReturnUserEnabled());
  const [marketingId, setMarketingId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [isReturnUser, setIsReturnUser] = useState(false);

  useEffect(() => {
    if (!isRepoSideReturnUserEnabled()) {
      gatsbyHydrationManagedByHook = false;

      setMarketingId(null);
      setUserName(null);
      setIsReturnUser(false);
      setLoading(false);

      dispatchGatsbyHydrationComplete();

      return;
    }
    
    let mounted = true;
    gatsbyHydrationManagedByHook = true;

    const init = async () => {
      try {
        // Bucket gate at the outermost layer for hardcoded return-user experiences.
        // This uses the shared personalization bucket resolver also used by alerts.
        const bucket = await getPersonalizationBucket();

        if (!mounted) return;

        if (bucket === null) {

          postReturnUserFallbackUiLog('return-user');

          setIsReturnUser(false);
          setLoading(false);
          return;
        }

        if (!isRepoBucket(bucket)) {
          setIsReturnUser(false);
          setLoading(false);
          return;
        }

        setLoading(true);

        const cc = normalizeParamValue(getQueryParam('cc'));
        const ref = normalizeParamValue(getQueryParam('ref'));

        const normalizedBlockedCcParams = blockedCcParams?.map(normalizeParamValue);
        const normalizedIncludeRefParams = includeRefParams?.map(normalizeParamValue);

        // Determine page mode
        const isRefOnlyPage = Boolean(normalizedIncludeRefParams && !normalizedBlockedCcParams);

        let isAllowed = true;

        // CC gating (only if page provided CC list)
        if (cc && normalizedBlockedCcParams) {
          isAllowed = !normalizedBlockedCcParams.includes(cc);
        }

        // REF gating (value check)
        if (isAllowed && ref && normalizedIncludeRefParams) {
          isAllowed = normalizedIncludeRefParams.includes(ref);
        }

        // REF gating (value check)
        if (isAllowed && isRefOnlyPage && !ref) {
          isAllowed = false;
        }

        if (!isAllowed) {
          setIsReturnUser(false);
          setLoading(false);
          return;
        }

        const existingSession = getExistingValidSession();
        const mid = getMarketingId() ?? existingSession?.marketingId ?? null;
        if (!mounted) {
          setLoading(false);
          return;
        }

        if (!mid) {
          setLoading(false);
          return;
        }
        setMarketingId(mid);

        const sessionToken =
          existingSession && existingSession.marketingId === mid ? existingSession.token : null;
        const token = sessionToken ?? (await getToken({ mid }));
        if (!mounted || !token) {
          setLoading(false);
          return;
        }

        // return user confirmed
        setIsReturnUser(true);
        setLoading(false);

        // non‑blocking enrichment
        const name = await getUserName(token);
        if (mounted) setUserName(name);
      } catch (err) {
        console.error('Return user flow failed', err);
        if (mounted) {
          setIsReturnUser(false);
          setLoading(false);
        }
      }
    };
    init();
    return () => {
      mounted = false;
    };
  }, [blockedCcParams, includeRefParams]);

  useEffect(() => {
    if (!loading) {
      dispatchGatsbyHydrationComplete();
    }
  }, [loading]);

  return {
    loading,
    marketingId,
    userName,
    isReturnUser
  };
}

function getExistingSessionToken(): string | null {
  try {
    const sessionStr = sessionStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: UserSession = JSON.parse(sessionStr);
    return isTokenValid(session) ? session.token : null;
  } catch {
    return null;
  }
}

function getExistingValidSession(): UserSession | null {
  try {
    const sessionStr = sessionStorage.getItem(SESSION_KEY);
    if (!sessionStr) return null;

    const session: UserSession = JSON.parse(sessionStr);
    return isTokenValid(session) ? session : null;
  } catch {
    return null;
  }
}

function clearAlertsCampaignGlobals() {
  if (typeof window === 'undefined') return;

  delete (window as any).ecsTstCampaign;
  delete (window as any).ecsTstDecibelRecipe;
}

function normalizeTargetAlertNumber(value: string | number | null): number | null {
  if (value === null) return null;

  if (value === '99+') {
    return 100;
  }

  const count = Number(value);
  return Number.isFinite(count) ? count : null;
}

export function useAlerts(enabled = true) {
  const [loading, setLoading] = useState(false);
  const [marketingId, setMarketingId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | null>(null);
  const [alertsCount, setAlertsCount] = useState<number | null>(null);
  const [isReturnUser, setIsReturnUser] = useState(false);
  const [showTargetAlertsBell, setShowTargetAlertsBell] = useState(false);
  const [targetAlertText, setTargetAlertText] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    let targetAlertsTimer: number | null = null;
    let shouldClearRepoCampaignGlobals = false;

    const resetAlertsState = () => {
      setIsReturnUser(false);
      setTargetAlertText(null);
      setAlertsCount(null);
      setLoading(false);
    };

    const initAlerts = async () => {
      if (!enabled) {
        clearAlertsCampaignGlobals();
        setShowTargetAlertsBell(false);
        resetAlertsState();
        return;
      }

      // Use the same shared personalization bucket resolver as return-user.
      const bucket = await getPersonalizationBucket();

      if (!mounted) return;

      const inRepoBucket = isRepoBucket(bucket);
      const inTargetHandledBucket = isTargetHandledBucket(bucket);

      setShowTargetAlertsBell(false);

      if (!inRepoBucket) {
        if (inTargetHandledBucket) {
          setIsReturnUser(false);
          setTargetAlertText(null);
          setAlertsCount(null);
          setLoading(false);

          setTimeout(() => {
            if (!window.gatsbyHydrationComplete) {
              dispatchGatsbyHydrationComplete();
            }
          }, 250);

          const alertTimingFieldsPromise = getAlertTimingFields();

          let attempts = 0;
          const maxAttempts = 300; // 30 seconds at 100ms

          const applyTargetAlerts = (): boolean => {
            if (!mounted) return true;

            const targetAlerts = window.alertsNavTest;

            if (!targetAlerts?.show) {
              return false;
            }

            const normalizedCount = normalizeTargetAlertNumber(targetAlerts.number);

            setMarketingId(getMarketingId());
            setAlertsCount(normalizedCount);
            setTargetAlertText(targetAlerts.drpText ?? null);
            setIsReturnUser(true);
            setShowTargetAlertsBell(true);

            alertTimingFieldsPromise.then((alertTimingFields) => {
              if (!mounted) return;

              postUiLog(EventName.alerts, {
                placement: 'nav-public-header-alerts',
                category: 'alerts-displayed',
                value: normalizedCount,
                ...alertTimingFields
              });
            });

            setLoading(false);

            if (targetAlertsTimer !== null) {
              window.clearInterval(targetAlertsTimer);
              targetAlertsTimer = null;
            }

            return true;
          };

          // Check immediately in case Target already populated window.alertsNavTest.
          if (applyTargetAlerts()) {
            return;
          }

          targetAlertsTimer = window.setInterval(() => {
            if (applyTargetAlerts()) {
              return;
            }

            attempts += 1;

            if (attempts >= maxAttempts) {
              resetAlertsState();

              if (targetAlertsTimer !== null) {
                window.clearInterval(targetAlertsTimer);
                targetAlertsTimer = null;
              }
            }
          }, 100);

          return;
        }

        clearAlertsCampaignGlobals();

        postReturnUserFallbackUiLog('nav-public-header-alerts');

        resetAlertsState();

        dispatchGatsbyHydrationComplete();

        return;
      }

      shouldClearRepoCampaignGlobals = true;
      gatsbyHydrationManagedByHook = true;
      setLoading(true);
      setTargetAlertText(null);

      try {
        const mid = getMarketingId();
        if (!mounted) return;

        setMarketingId(mid);

        // NO MID → NO ALERTS
        if (!mid) {
          clearAlertsCampaignGlobals();
          setIsReturnUser(false);
          return;
        }

        // Use existing token when available; otherwise fetch/reuse the shared in-flight token request.
        const existingToken = getExistingSessionToken();
        const token = existingToken ?? (await getToken({ mid }));

        if (!mounted || !token) {
          clearAlertsCampaignGlobals();
          setIsReturnUser(false);
          return;
        }

        // Confirmed return user
        setIsReturnUser(true);

        // Now (and only now) set campaign globals
        window.ecsTstCampaign = {
          campaign: 'RUHCTST',
          recipe: 'B1',
          persist: true
        };

        window.ecsTstDecibelRecipe = `${window.ecsTstCampaign.campaign}-${window.ecsTstCampaign.recipe}-${getBreakpointIdentifier()}-XXXXX`;

        // Ensure OP/Decibel data is applied before event_returnUserAlert fires.
        if (window.opAppend) {
          window.opAppend();
        }

        const name = await getUserName(token);
        if (!mounted) return;
        setUserName(name);

        const count = await getAlertsCount(token);

        if (!mounted) return;

        setAlertsCount(count ?? 0);

        const alertTimingFields = await getAlertTimingFields();

        postUiLog(EventName.alerts, {
          placement: 'nav-public-header-alerts',
          category: 'alerts-displayed',
          value: count ?? 0,
          ...alertTimingFields
        });
      } catch (err) {
        clearAlertsCampaignGlobals();
        console.error('Failed to fetch alerts', err);

        if (mounted) {
          setIsReturnUser(false);
          setTargetAlertText(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);

          setTimeout(() => {
            dispatchGatsbyHydrationComplete();
          }, 0);
        }
      }
    };

    initAlerts();

    return () => {
      mounted = false;

      if (targetAlertsTimer !== null) {
        window.clearInterval(targetAlertsTimer);
      }

      if (shouldClearRepoCampaignGlobals) {
        clearAlertsCampaignGlobals();
      }
    };
  }, [enabled]);

  const alertText = targetAlertText
    ? targetAlertText
    : !isReturnUser
      ? 'Sign in to see your alert notifications.'
      : userName
        ? alertsCount && alertsCount > 0
          ? `${userName}, you have ${alertsCount > 99 ? '99+' : alertsCount} unread alert${alertsCount === 1 ? '' : 's'} to review.`
          : `${userName}, sign in to see your alert notifications.`
        : 'Sign in to see your alert notifications.';

  return {
    loading,
    marketingId,
    userName,
    alertsCount,
    isReturnUser,
    alertText,
    showTargetAlertsBell
  };
}